import { useCallback, useEffect, useRef, useState } from "react";

const POLL_MS = 60_000;

// Public-safe fallback used only when the gateway itself is unreachable
// from the browser. Matches the canonical response shape.
const FALLBACK = {
  status: "unreachable",
  reason: "gateway_unreachable",
  message: "Runtime gateway unreachable from the browser.",
  mode: "unset",
  mode_configured: false,
  endpoint_configured: false,
  token_configured: false,
  fallback_allowed: false,
  node_id: null,
  version: null,
  health: "unavailable",
  signing_service_available: false,
  last_check: null,
};

// Polls the Lab Pathway runtime gateway for canonical KATLAS status. The
// token never crosses to the browser — only public-safe status fields do.
export function useRuntimeStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) {
      if (aliveRef.current) {
        setStatus({ ...FALLBACK, reason: "no_backend_url" });
        setLoading(false);
      }
      return;
    }
    try {
      const r = await fetch(`${base}/api/runtime/status`, {
        headers: { Accept: "application/json" },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (!aliveRef.current) return;
      setStatus(data);
      setError(null);
    } catch (e) {
      if (!aliveRef.current) return;
      setStatus({
        ...FALLBACK,
        last_check: new Date().toISOString(),
      });
      setError(String(e));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(t);
    };
  }, [fetchOnce]);

  return { status, loading, error, refresh: fetchOnce };
}
